<template>
  <v-row justify="center">
    <!-- loader -->
    <v-skeleton-loader v-if="loader" class="my-3" width="800" type="article, actions" />
    <!-- end loader -->

    <v-card v-else class="transparent my-3 mx-auto" rounded outlined width="800">
      <v-card-text style="height: calc(100vh - 175px); overflow-y: auto;">
        <v-stepper class="transparent" v-model="step" alt-labels>
          <v-stepper-header class="align-center">

            <img class="mt-n2 ml-6" :src="require('@/assets/appLogo-color--Onlight.svg')" width="100"/>

            <v-stepper-step step="1" />
            <v-divider />
            <v-stepper-step step="2" />
            <v-divider />
            <v-stepper-step step="3" />
            <v-divider />
            <v-stepper-step step="4" />
          </v-stepper-header>
          <v-stepper-items>
            <!-- welcome -->
            <v-stepper-content class="py-2" step="1">
              <span class="d-block text-h5 font-weight-medium">Bienvenido a Piriod</span>
              <span class="d-block mb-5 body-1 text--primary">Ahora tendrás el poder de agilizar, mantener y escalar tu negocio de suscripciones.</span>

              <span class="d-block body-1 font-weight-medium">Piriod te ayudará a:</span>

              <v-row>
                <v-col cols="6">
                  <v-list class="transparent">
                    <v-list-item class="px-0 py-1">
                      <v-list-item-title>
                        <v-avatar color="indigo lighten-5" size="28"><v-icon small color="primary">mdi-account-multiple</v-icon></v-avatar>
                        <span class="ml-2 body-1 text--secondary font-weight-regular">Incorporar y gestionar suscriptores</span>
                      </v-list-item-title>
                    </v-list-item>
                    <v-list-item class="px-0 py-1">
                      <v-list-item-title>
                        <v-avatar color="indigo lighten-5" size="28"><v-icon small color="primary">mdi-credit-card-sync</v-icon></v-avatar>
                        <span class="ml-2 body-1 text--secondary font-weight-regular">Recibir pagos recurrentes</span>
                      </v-list-item-title>
                    </v-list-item>
                    <v-list-item class="px-0 py-1">
                      <v-list-item-title class="overflow-visible">
                        <v-avatar color="indigo lighten-5" size="28"><v-icon small color="primary">mdi-cash-edit</v-icon></v-avatar>
                        <span class="ml-2 body-1 text--secondary font-weight-regular">Experimentar diferentes modos de cobro</span>
                      </v-list-item-title>
                    </v-list-item>
                    <v-list-item class="px-0 py-1">
                      <v-list-item-title>
                        <v-avatar color="indigo lighten-5" size="28"><v-icon small color="primary">mdi-chart-line</v-icon></v-avatar>
                        <span class="ml-2 body-1 text--secondary font-weight-regular">Analizar detalladamente tus ingresos</span>
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-col>
                <v-col cols="6">
                  <v-img contain :src="require('@/assets/appAsset-setup--Check.svg')" height="" />
                </v-col>
              </v-row>
            </v-stepper-content>
            <!-- end welcome -->

            <!-- team -->
            <v-stepper-content class="py-2" step="2">
              <span class="d-block text-h5 font-weight-medium">¿Qué describe mejor el trabajo que haces?</span>
              <span class="d-block mb-5 body-1 text--primary">Ayúdanos a personalizar tu experiencia inicial en Piriod.</span>

              <v-row class="px-1">
                <template v-for="(d, i) in roles">
                  <v-col cols="4" :key="`v-sheet--${i}`">
                    <v-hover v-slot:default="{ hover }">
                      <v-sheet class="pa-4 text-center cursor-pointer" :class="{ 'v-sheet--active': (newUser.role === d.id) }" @click="newUser.role=d.id" outlined rounded="lg" width="100%" :elevation="hover ? '3' : '0'">
                        <v-avatar color="indigo lighten-5" size="32"><v-icon color="primary" >{{d.icon}}</v-icon></v-avatar>
                        <span class="d-block mt-3 text--secondary">{{d.label}}</span>
                      </v-sheet>
                    </v-hover>
                  </v-col>
                </template>
              </v-row>
            </v-stepper-content>
            <!-- end team -->

            <!-- channel -->
            <v-stepper-content class="py-2" step="3">
              <span class="d-block mb-5 text-h5 font-weight-medium">¿Cómo supiste de Piriod?</span>

              <template v-for="(d, i) in channels">
                <v-checkbox class="d-inline-block my-2 mx-4 ml-0 outlined" :key="i" v-model="newUser.channels" color="primary" hide-details :label="d.label" :ripple="false" :value="d.id" />
              </template>
            </v-stepper-content>
            <!-- end channel -->

            <!-- organization -->
            <v-stepper-content class="py-2" step="4">
              <span class="d-block text-h5 font-weight-medium">Crea tu organización</span>
              <span class="d-block mb-5 body-1 text--primary">Aquí es donde tu equipo va a colaborar.</span>

              <v-list class="transparent" dense>
                <v-list-item class="px-0">
                  <v-list-item-content>
                    <v-list-item-title class="ml-1 body-1 text--secondary font-weight-regular overflow-visible">Nombre de la organización</v-list-item-title>
                    <v-list-item-subtitle>
                      <v-col cols="11" class="py-0 pl-0">
                        <v-text-field v-model.trim="$v.newAccount.name.$model" :error="$v.newAccount.name.$error" background-color="white" outlined required single-line dense maxlength="64" hide-details />
                      </v-col>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item class="px-0">
                  <v-list-item-content>
                    <v-list-item-subtitle>
                      <v-row class="px-3">
                        <v-col cols="6" class="py-0 pl-0">
                          <span class="ml-1 body-1 text--secondary font-weight-regular">¿Desde que país opera la organización?</span>
                          <v-select v-model="$v.newAccount.country.$model" :error="$v.newAccount.country.$error" :items="baseCountries" single-line item-text="name" item-value="id" hide-details dense height="30" outlined background-color="white" />
                        </v-col>
                        <v-col cols="5" class="py-0 pl-0">
                          <span class="ml-1 body-1 text--secondary font-weight-regular">ID Fiscal <span class="body-2 text--primary">(VAT, RUT, RUC, RFC o DNI)</span></span>
                          <v-text-field v-model.trim="$v.newAccount.tax_id.$model" :error="$v.newAccount.tax_id.$error" v-dni="newAccount.country" background-color="white" outlined required single-line dense maxlength="64" hide-details />
                        </v-col>
                      </v-row>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item class="px-0">
                  <v-list-item-content>
                    <v-list-item-title class="ml-1 body-1 text--secondary font-weight-regular overflow-visible">¿Qué describe mejor la industria de tu organización?</v-list-item-title>
                    <v-list-item-subtitle>
                      <v-col cols="6" class="py-0 pl-0">
                        <v-select v-model="$v.newAccount.economic_sector.$model" :error="$v.newAccount.economic_sector.$error" :items="economicSectors" single-line item-text="label" item-value="id" hide-details dense height="30" outlined background-color="white" />
                      </v-col>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item class="px-0">
                  <v-list-item-content>
                    <v-list-item-title class="ml-1 body-1 text--secondary font-weight-regular overflow-visible">¿Cuántas personan trabajan en tu organización?</v-list-item-title>
                    <v-list-item-subtitle>
                      <v-col cols="3" class="py-0 pl-0">
                        <v-select v-model="$v.newAccount.employee_quantity.$model" :error="$v.newAccount.employee_quantity.$error" :items="employeeQuantities" single-line item-text="label" item-value="id" hide-details dense height="30" outlined background-color="white" />
                      </v-col>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item class="px-0">
                  <v-list-item-content>
                    <v-list-item-title class="ml-1 body-1 text--secondary font-weight-regular overflow-visible">¿Cómo cobra tu organización?</v-list-item-title>
                    <v-list-item-subtitle>
                      <v-col cols="6" class="py-0 pl-0">
                        <v-select v-model="$v.newAccount.pricing_model.$model" :error="$v.newAccount.pricing_model.$error" :items="pricingModels" single-line item-text="label" item-value="id" hide-details dense height="30" outlined background-color="white" />
                      </v-col>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-stepper-content>
            <!-- end organization -->
          </v-stepper-items>
        </v-stepper>
      </v-card-text>
      <v-card-actions class="pa-10">
        <v-btn class="pr-6 pl-3" @click="step-=1" v-if="step!==1" large :loading="$store.state.accounts.createLoader" outlined :ripple="false"><v-icon left size="22">mdi-arrow-left</v-icon>Volver</v-btn>
        <v-spacer/>

        <!-- error messages -->
        <span class="mr-6 red--text" v-if="step === 2 & $v.newUser.role.$error">Selecciona una de las opciones.</span>
        <span class="mr-6 red--text" v-if="step === 3 & $v.newUser.channels.$error">Selecciona al menos una de las opciones.</span>
        <span class="mr-6 red--text" v-if="step === 4 & $v.newAccount.$error">Corrige los campos marcados.</span>
        <!-- end error messages -->

        <v-btn class="pr-3 pl-6" @click="nextStep" color="primary" large :loading="$store.state.accounts.createLoader" :ripple="false">Continuar<v-icon dark right size="22">mdi-arrow-right</v-icon></v-btn>
      </v-card-actions>
    </v-card>
  </v-row>
</template>
<script>
import { mapState } from 'vuex'
import { required, requiredIf, minLength, maxLength } from 'vuelidate/lib/validators'
import { rutValidator } from 'vue-dni'
import GenericViewMixin from '@/mixins/GenericViewMixin'

export default {
  mixins: [
    GenericViewMixin
  ],
  data: () => ({
    channels: [
      { id: 'search', label: 'Buscador (Google, Bing, etc.)' },
      { id: 'referrer', label: 'Amigo o colega' },
      { id: 'blog', label: 'Blog de Piriod' },
      { id: 'linkedin', label: 'LinkedIn' },
      { id: 'youtube', label: 'YouTube' },
      { id: 'facebook', label: 'Facebook' },
      { id: 'instagram', label: 'Instagram' },
      { id: 'podcast', label: 'Podcast' },
      { id: 'webinar', label: 'Webinar' },
      { id: 'review_site', label: 'Sitios de reseñas de software' },
      { id: 'other', label: 'Otros' }
    ],
    economicSectors: [
      { id: 'b2b_saas', label: 'B2B / SaaS' },
      { id: 'digital_media', label: 'Medios digitales' },
      { id: 'nonprofit', label: 'Organizaciones sin fines de lucro' },
      { id: 'consulting', label: 'Servicios profesionales y/o consultoría' },
      { id: 'banks', label: 'Banco y servicios financieros' },
      { id: 'insurance', label: 'Seguros' },
      { id: 'health', label: 'Salud' },
      { id: 'edtech', label: 'Ed-tech' },
      { id: 'telecom', label: 'Telecomunicaciones' },
      { id: 'retail', label: 'Retail / E-commerce' },
      { id: 'logistics', label: 'Logística' },
      { id: 'trips', label: 'Viajes' },
      { id: 'other', label: 'Otro' }
    ],
    employeeQuantities: [
      { id: '1_19', label: '1 - 19' },
      { id: '20_49', label: '20 - 49' },
      { id: '50_99', label: '50 - 99' },
      { id: '100_250', label: '100 - 250' },
      { id: '251_500', label: '251 - 500' },
      { id: '501_1500', label: '501 - 1500' },
      { id: '1500+', label: 'Más de 1500' }
    ],
    newAccount: {
      country: '',
      economic_sector: '',
      name: '',
      tax_id: '',
      pricing_model: '',
      employee_quantity: ''
    },
    newUser: {
      channels: [],
      email: '',
      role: ''
    },
    pricingModels: [
      { id: 'recurring', label: 'Cobro recurrente por suscripción o membresía' },
      { id: 'transactional', label: 'Transaccional (intercambio de producto por dinero)' },
      { id: 'commission', label: 'Comisionista' },
      { id: 'dont_know', label: 'No lo sé' }
    ],
    roles: [
      { id: 'finance', icon: 'mdi-bank', label: 'Finanzas / Contabilidad' },
      { id: 'engineering', icon: 'mdi-code-greater-than', label: 'Ingeniería' },
      { id: 'founder_excecutive', icon: 'mdi-lightbulb-on', label: 'Fundador / Ejecutivo' },
      { id: 'growth', icon: 'mdi-chart-box', label: 'Growth' },
      { id: 'sales', icon: 'mdi-briefcase-plus', label: 'Ventas' },
      { id: 'ops', icon: 'mdi-cog', label: 'Operaciones' },
      { id: 'other', icon: 'mdi-help-box', label: 'Otro' }
    ],
    intercomHash: '',
    loader: true,
    step: 1
  }),
  computed: mapState({
    accountsCount: state => state.accounts.accountsCount,
    trace: state => state.auth.trace
  }),
  watch: {
    'newAccount.country' () {
      this.newAccount.tax_id = ''
    }
  },
  created () {
    // check if user belongs to any organization
    // and redirect user to choose one if it's have
    this.$store.dispatch('accounts/LIST', {
      resource: 'accounts'
    })
    .then(() => {
      if (this.accountsCount) {
        this.$router.push({ name: 'AccountChoose' })

        return false
      }

      // set user localization
      this.$store.dispatch('auth/RETRIEVE_TRACE')
      .then(() => {
        this.newAccount.country = this.trace.loc
      })

      // set user email
      this.$store.dispatch('auth/GET_USER')
      .then((response) => {
        this.newUser.email = response.data.email
        this.intercomHash = response.data.intercom.hash
      })
    })
    .finally(() => {
      setTimeout(() => {
        this.loader = false
      }, 500)
    })
  },
  methods: {
    createAccount () {
      this.$store.dispatch('accounts/CREATE', {
        resource: 'accounts',
        payload: {
          country: this.newAccount.country,
          name: this.newAccount.name,
          tax_id: this.newAccount.tax_id
        }
      })
      .then((response) => {
        const account = response.data

        // identify objects
        this.identify(account)

        // set account in store
        this.$store.commit('auth/SET_ACCOUNT', {
          account: account,
          redirect: false
        })

        // redirect to quickstart
        this.$router.push({ name: 'AccountQuickstart' })
      })
      .catch((error) => {
        this.$dialog.message.error(error.response.data.tax_id[0] || 'Error al crear la cuenta')
      })
    },
    identify (account) {
      /**
       * identifies user and the organization
       * it runs as long as the env is production
       */
      if (process.env.NODE_ENV !== 'production') {
        return false
      }

      const userTraits = {
        email: this.newUser.email,
        channels: this.newUser.channels.join(';'),
        role: this.newUser.role
      }

      const groupTraits = {
        createdAt: account.created,
        economic_sector: this.newAccount.economic_sector,
        employee_quantity: this.newAccount.employee_quantity,
        name: this.newAccount.name,
        pricing_model: this.newAccount.pricing_model,
        tax_id: account.tax_id
      }

      // user
      this.$analytics.identify(this.newUser.email, userTraits)

      // group
      this.$analytics.group(account.id, groupTraits)
    },
    nextStep () {
      if (this.step === 1) {
        this.step += 1
        return false
      }

      if (this.step === 2) {
        this.$v.newUser.role.$touch()

        if (this.$v.newUser.role.$invalid) {
          return false
        }

        this.step += 1
        return false
      }

      if (this.step === 3) {
        this.$v.newUser.channels.$touch()

        if (this.$v.newUser.channels.$invalid) {
          return false
        }

        this.step += 1
        return false
      }

      if (this.step === 4) {
        this.$v.newAccount.$touch()

        if (this.$v.newAccount.$invalid) {
          return false
        }

        this.createAccount()
        return false
      }
    }
  },
  validations: {
    newAccount: {
      country: {
        required
      },
      economic_sector: {
        required
      },
      name: {
        required,
        minLength: minLength(6),
        maxLength: maxLength(64)
      },
      tax_id: {
        required: requiredIf(function () {
          const country = this.baseCountries.find(country => country.id === this.newAccount.country)

          if (!country) {
            return false
          }

          return country.hasRegulation
        }),
        taxId: function (val) {
          if (this.newAccount.country === 'CL') {
            return rutValidator(val)
          }

          return true
        },
        maxLength: maxLength(32)
      },
      employee_quantity: {
        required
      },
      pricing_model: {
        required
      }
    },
    newUser: {
      channels: {
        required
      },
      role: {
        required
      }
    }
  }
}
</script>

<style>
  .v-input--checkbox {
    border: 1px solid #D9E0EA;
    border-radius: 12px;
    padding: 0 !important;
  }
  .v-input--checkbox .v-input__slot {
    padding: 12px 16px;
  }

  .v-sheet--outlined.v-sheet--active {
    border: 1px solid #3F51B5 !important;
  }

  .v-application .elevation-0.v-sheet--active,
  .v-application .elevation-3:hover.v-sheet--active {
    box-shadow: 0px 0px 0px 3px #CCE1FF !important;
  }
</style>